/**
 * Edit mode overlay for curve objects — renders control points, handles,
 * handle lines, and the evaluated spline curve.
 *
 * Uses the same InstancedMesh + billboard shader pattern as EditModeRenderer
 * for screen-space constant-size dots with raycasting hit targets.
 */

import { type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import { evaluateCurve } from "../modeling/curvemath.ts";
import type { CurveData } from "../modeling/types.ts";
import {
  _dummy,
  createDotGeometry,
  dotBaseScale,
  dotMaterial,
  HIT_SCREEN_PX,
  HIT_SPHERE,
  setDotColor,
  VERTEX_HIT_RADIUS,
  VERTEX_RADIUS,
  VERTEX_SCREEN_PX,
} from "./editDots.ts";
import { useDispose } from "./hooks/useDispose.ts";
import {
  screenToObjectLocalRadius,
  screenToWorldRadius,
} from "./screenRadius.ts";

/** Curve-specific dot material — same billboard shader as mesh dots but with
 *  depth testing disabled so control points always draw over the bevel surface. */
const curveDotMaterial = dotMaterial.clone();
curveDotMaterial.depthTest = false;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Control points are slightly larger than mesh vertices. */
const CONTROL_POINT_SCREEN_PX = 6;
/** Handles are slightly smaller. */
const HANDLE_SCREEN_PX = 4;

const _colorControlDefault = new THREE.Color("#55aaaa");
const _colorControlSelected = new THREE.Color("#ff9922");
const _colorHandleDefault = new THREE.Color("#999999");
const _colorHandleSelected = new THREE.Color("#ff9922");
const _dotWorldPos = new THREE.Vector3();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CurveEditOverlay({
  curveData,
  editVertices,
  selectedVertices,
  objectPosition,
  objectRotation,
  objectScale,
}: {
  curveData: CurveData;
  editVertices: Float32Array;
  selectedVertices: Set<number>;
  objectPosition: [number, number, number];
  objectRotation: [number, number, number];
  objectScale: [number, number, number];
}) {
  const handleEditClick = useStore((s) => s.handleEditClick);
  const { camera, size: viewportSize } = useThree();
  const n = curveData.points.length;

  // --- Determine which handles are visible ---
  // A handle is visible when its parent control point or the handle itself is selected.
  const visibleHandles = useMemo(() => {
    const visible = new Set<number>();
    for (let i = 0; i < n; i++) {
      const coIdx = i * 3;
      const hlIdx = i * 3 + 1;
      const hrIdx = i * 3 + 2;
      if (
        selectedVertices.has(coIdx) ||
        selectedVertices.has(hlIdx) ||
        selectedVertices.has(hrIdx)
      ) {
        visible.add(hlIdx);
        visible.add(hrIdx);
      }
    }
    return visible;
  }, [n, selectedVertices]);

  // --- Build combined dot list: control points + visible handles ---
  // Each entry maps an instance index to an edit vertex index.
  const dotEntries = useMemo(() => {
    const entries: Array<{ editIdx: number; isControl: boolean }> = [];
    // Control points first
    for (let i = 0; i < n; i++) {
      entries.push({ editIdx: i * 3, isControl: true });
    }
    // Then visible handles
    for (let i = 0; i < n; i++) {
      const hlIdx = i * 3 + 1;
      const hrIdx = i * 3 + 2;
      if (visibleHandles.has(hlIdx))
        entries.push({ editIdx: hlIdx, isControl: false });
      if (visibleHandles.has(hrIdx))
        entries.push({ editIdx: hrIdx, isControl: false });
    }
    return entries;
  }, [n, visibleHandles]);

  const dotCount = dotEntries.length;

  // --- Visual instanced mesh ---
  const visualRef = useRef<THREE.InstancedMesh>(null);
  const hitRef = useRef<THREE.InstancedMesh>(null);
  const lastHitScaleRef = useRef(0);

  // Visual dot geometry with per-instance scale attributes
  const visualGeo = useMemo(() => createDotGeometry(dotCount), [dotCount]);
  useDispose(visualGeo);

  const computeVisualDotScale = useCallback(
    (object: THREE.Object3D) => {
      object.getWorldPosition(_dotWorldPos);
      return (
        screenToWorldRadius(
          VERTEX_SCREEN_PX,
          _dotWorldPos,
          camera,
          viewportSize.height,
        ) / VERTEX_RADIUS
      );
    },
    [camera, viewportSize.height],
  );

  const computeHitDotScale = useCallback(
    (object: THREE.Object3D) => {
      object.getWorldPosition(_dotWorldPos);
      return (
        screenToObjectLocalRadius(
          HIT_SCREEN_PX,
          _dotWorldPos,
          object.matrixWorld,
          camera,
          viewportSize.height,
        ) / VERTEX_HIT_RADIUS
      );
    },
    [camera, viewportSize.height],
  );

  // Set per-instance size factors (control points vs handles)
  useEffect(() => {
    const factorAttr = visualGeo.getAttribute(
      "instanceSizeFactor",
    ) as THREE.InstancedBufferAttribute;
    if (!factorAttr || factorAttr.count < dotCount) return;
    for (let i = 0; i < dotCount; i++) {
      const entry = dotEntries[i];
      factorAttr.setX(
        i,
        entry.isControl
          ? CONTROL_POINT_SCREEN_PX / VERTEX_SCREEN_PX
          : HANDLE_SCREEN_PX / VERTEX_SCREEN_PX,
      );
    }
    factorAttr.needsUpdate = true;
  }, [visualGeo, dotEntries, dotCount]);

  // Position from edit vertex index
  const getPos = useCallback(
    (editIdx: number): [number, number, number] => {
      const pi = Math.floor(editIdx / 3);
      const component = editIdx % 3; // 0=co, 1=handleLeft, 2=handleRight
      const base = pi * 9 + component * 3;
      return [
        editVertices[base],
        editVertices[base + 1],
        editVertices[base + 2],
      ];
    },
    [editVertices],
  );

  // Build translation-only instance matrices (scale handled by GPU uniform)
  const rebuildPositions = useCallback(() => {
    const vis = visualRef.current;
    const hit = hitRef.current;
    if (!vis || dotCount === 0) return;

    for (let i = 0; i < dotCount; i++) {
      const entry = dotEntries[i];
      const pos = getPos(entry.editIdx);
      _dummy.position.set(pos[0], pos[1], pos[2]);
      _dummy.scale.setScalar(1);
      _dummy.updateMatrix();
      vis.setMatrixAt(i, _dummy.matrix);
    }
    vis.instanceMatrix.needsUpdate = true;

    if (hit) {
      const hitScale = computeHitDotScale(hit);
      for (let i = 0; i < dotCount; i++) {
        const entry = dotEntries[i];
        const pos = getPos(entry.editIdx);
        _dummy.position.set(pos[0], pos[1], pos[2]);
        _dummy.scale.setScalar(hitScale || 1);
        _dummy.updateMatrix();
        hit.setMatrixAt(i, _dummy.matrix);
      }
      hit.instanceMatrix.needsUpdate = true;
    }
  }, [dotEntries, dotCount, getPos, computeHitDotScale]);

  // Rebuild on vertex data change
  useEffect(() => {
    rebuildPositions();
  }, [rebuildPositions]);

  // Per-instance colors — written to the geometry-level dotColor attribute
  useEffect(() => {
    if (dotCount === 0) return;
    for (let i = 0; i < dotCount; i++) {
      const entry = dotEntries[i];
      const selected = selectedVertices.has(entry.editIdx);
      const color = entry.isControl
        ? selected
          ? _colorControlSelected
          : _colorControlDefault
        : selected
          ? _colorHandleSelected
          : _colorHandleDefault;
      setDotColor(visualGeo, i, color);
    }
    const attr = visualGeo.getAttribute(
      "dotColor",
    ) as THREE.InstancedBufferAttribute;
    attr.needsUpdate = true;
  }, [dotEntries, dotCount, selectedVertices, visualGeo]);

  // Screen-space constant sizing — just update the GPU uniform (O(1))
  useFrame(() => {
    if (dotCount === 0) return;
    const vis = visualRef.current;
    if (!vis) return;

    dotBaseScale.value = computeVisualDotScale(vis);

    // Hit spheres still need CPU-side scale for raycasting.
    const hit = hitRef.current;
    const hitScale = hit ? computeHitDotScale(hit) : 0;
    const prevHit = lastHitScaleRef.current;
    if (prevHit === 0 || Math.abs(hitScale - prevHit) / prevHit > 0.02) {
      lastHitScaleRef.current = hitScale;
      if (hit && dotCount > 0) {
        const arr = hit.instanceMatrix.array as Float32Array;
        for (let i = 0; i < dotCount; i++) {
          const off = i * 16;
          arr[off] = hitScale;
          arr[off + 5] = hitScale;
          arr[off + 10] = hitScale;
        }
        hit.instanceMatrix.needsUpdate = true;
      }
    }
  });

  // --- Click handling ---
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  const TAP_THRESHOLD = 8;

  const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    pointerDownPos.current = {
      x: e.nativeEvent.clientX,
      y: e.nativeEvent.clientY,
    };
  }, []);

  const handlePointerUp = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      const down = pointerDownPos.current;
      pointerDownPos.current = null;
      if (!down) return;
      const dx = e.nativeEvent.clientX - down.x;
      const dy = e.nativeEvent.clientY - down.y;
      if (dx * dx + dy * dy > TAP_THRESHOLD * TAP_THRESHOLD) return;

      const instanceId = (e as any).instanceId as number | undefined;
      if (instanceId == null || instanceId >= dotEntries.length) return;

      const entry = dotEntries[instanceId];
      e.stopPropagation();

      handleEditClick(
        { vertices: [entry.editIdx], edge: undefined, faceIndex: undefined },
        {
          shift: e.nativeEvent.shiftKey,
          ctrl: e.nativeEvent.ctrlKey || e.nativeEvent.metaKey,
          alt: e.nativeEvent.altKey,
        },
      );
    },
    [dotEntries, handleEditClick],
  );

  // --- Evaluated curve line ---
  // Persistent Line + geometry buffer. During drag, `curveData` is replaced
  // every frame — allocating a fresh BufferGeometry + LineBasicMaterial each
  // time churned the GC and dominated overlay cost. Now we create once and
  // write positions in-place; the buffer only reallocates when sample count
  // changes (resolution or control-point count changes, not during a drag).
  const curveLineRef = useRef<THREE.Line | null>(null);
  if (!curveLineRef.current) {
    const geo = new THREE.BufferGeometry();
    const mat = new THREE.LineBasicMaterial({
      color: "#ffffff",
      opacity: 0.6,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    curveLineRef.current = new THREE.Line(geo, mat);
  }
  const curveLineObj = curveLineRef.current;

  useEffect(() => {
    const { positions } = evaluateCurve(curveData);
    const geo = curveLineObj.geometry as THREE.BufferGeometry;
    const need = positions.length * 3;
    let attr = geo.getAttribute("position") as THREE.BufferAttribute | null;
    if (!attr || attr.array.length !== need) {
      attr = new THREE.BufferAttribute(new Float32Array(need), 3);
      geo.setAttribute("position", attr);
    }
    const arr = attr.array as Float32Array;
    for (let i = 0; i < positions.length; i++) {
      arr[i * 3] = positions[i][0];
      arr[i * 3 + 1] = positions[i][1];
      arr[i * 3 + 2] = positions[i][2];
    }
    attr.needsUpdate = true;
    geo.boundingSphere = null;
  }, [curveData, curveLineObj]);

  useEffect(
    () => () => {
      const line = curveLineRef.current;
      if (line) {
        line.geometry.dispose();
        (line.material as THREE.Material).dispose();
        curveLineRef.current = null;
      }
    },
    [],
  );

  // --- Handle lines ---
  const handleLinesGeo = useMemo(() => {
    const segments: number[] = [];
    for (let i = 0; i < n; i++) {
      const hlIdx = i * 3 + 1;
      const hrIdx = i * 3 + 2;
      if (!visibleHandles.has(hlIdx) && !visibleHandles.has(hrIdx)) continue;

      const co = getPos(i * 3);
      if (visibleHandles.has(hlIdx)) {
        const hl = getPos(hlIdx);
        segments.push(co[0], co[1], co[2], hl[0], hl[1], hl[2]);
      }
      if (visibleHandles.has(hrIdx)) {
        const hr = getPos(hrIdx);
        segments.push(co[0], co[1], co[2], hr[0], hr[1], hr[2]);
      }
    }
    const geo = new THREE.BufferGeometry();
    if (segments.length > 0) {
      geo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(segments), 3),
      );
    }
    return geo;
  }, [n, visibleHandles, getPos]);
  useDispose(handleLinesGeo);

  const handleLineMat = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: "#888888",
        opacity: 0.5,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    [],
  );
  useDispose(handleLineMat);

  if (dotCount === 0) return null;

  return (
    <group
      position={objectPosition}
      rotation={objectRotation}
      scale={objectScale}
      userData={{ excludeFromExport: true }}
    >
      {/* Evaluated curve line */}
      <primitive object={curveLineObj} renderOrder={3} />

      {/* Handle lines */}
      {handleLinesGeo.attributes.position && (
        <lineSegments
          geometry={handleLinesGeo}
          material={handleLineMat}
          renderOrder={4}
        />
      )}

      {/* Visual dots (billboard circles — no depth test, always on top) */}
      <instancedMesh
        ref={visualRef}
        args={[visualGeo, curveDotMaterial, dotCount]}
        key={dotCount}
        raycast={() => {}}
        renderOrder={5}
      />

      {/* Invisible hit spheres for click detection */}
      <instancedMesh
        ref={hitRef}
        args={[HIT_SPHERE, undefined as any, dotCount]}
        key={`hit-${dotCount}`}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        renderOrder={5}
      >
        <meshBasicMaterial visible={false} />
      </instancedMesh>
    </group>
  );
}
